/**
 * Health Score Comparison Component
 * 
 * Shows comparative health scores across multiple franchise partners using bar charts
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import { Add as AddIcon, Compare as CompareIcon } from '@mui/icons-material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts'
import { healthScoreApi, HealthScoreComparison as HealthScoreComparisonData, ComparisonEntry } from '../../services/healthScoreApi'
import { HealthScoreBadge } from '../HealthScoreBadge'

interface HealthScoreComparisonProps {
  initialFpCodes?: string[]
  initialMetric?: 'overall' | 'product' | 'sale' | 'system'
  height?: number
  maxFranchises?: number
}

interface ChartDataPoint {
  fp_code: string
  franchise_name: string
  score: number | null
  band: string
  color: string
  displayName: string
}

const metricOptions = [
  { value: 'overall', label: 'Overall Health' },
  { value: 'product', label: 'Product Health' },
  { value: 'sale', label: 'Sale Health' },
  { value: 'system', label: 'System Health' }
]

const getBandColor = (band: string) => {
  switch (band) {
    case 'Good': return '#4caf50'
    case 'Watch': return '#ff9800'
    case 'Poor': return '#f44336'
    case 'Critical': return '#212121'
    default: return '#9e9e9e'
  }
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <Card sx={{ p: 1, maxWidth: 250 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Typography variant="subtitle2" gutterBottom>
            {data.franchise_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {data.fp_code}
          </Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">Score:</Typography>
            <Typography variant="body2" fontWeight="medium">
              {data.score !== null ? data.score.toFixed(1) : 'N/A'}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">Band:</Typography>
            <HealthScoreBadge
              band={data.band}
              color={data.color}
              size="small"
            />
          </Box>
        </CardContent>
      </Card>
    )
  }
  return null
}

export const HealthScoreComparison: React.FC<HealthScoreComparisonProps> = ({
  initialFpCodes = [],
  initialMetric = 'overall',
  height = 400,
  maxFranchises = 10
}) => {
  const [fpCodes, setFpCodes] = useState<string[]>(initialFpCodes)
  const [newFpCode, setNewFpCode] = useState<string>('')
  const [metric, setMetric] = useState<'overall' | 'product' | 'sale' | 'system'>(initialMetric)
  const [comparisonData, setComparisonData] = useState<HealthScoreComparisonData | null>(null)
  const [chartData, setChartData] = useState<ChartDataPoint[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchComparison = async () => {
    if (fpCodes.length < 2) {
      setError('At least 2 franchise partners required for comparison')
      return
    }

    setLoading(true)
    setError(null)
    
    try {
      const comparison = await healthScoreApi.compareScores(fpCodes, metric)
      setComparisonData(comparison)
      
      // Convert to chart format
      const processedData: ChartDataPoint[] = comparison.comparisons
        .sort((a, b) => {
          // Sort by score (nulls last), then by name
          if (a.score === null && b.score === null) return a.franchise_name.localeCompare(b.franchise_name)
          if (a.score === null) return 1
          if (b.score === null) return -1
          return a.score - b.score
        })
        .map(entry => ({
          ...entry,
          displayName: entry.franchise_name.length > 15 
            ? `${entry.franchise_name.substring(0, 12)}...` 
            : entry.franchise_name
        }))
      
      setChartData(processedData)
      
    } catch (err: any) {
      console.error('Error fetching comparison data:', err)
      setError(err.response?.data?.detail || 'Failed to fetch comparison data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (fpCodes.length >= 2) {
      fetchComparison()
    }
  }, [fpCodes, metric])

  const handleAddFpCode = () => {
    if (newFpCode.trim() && !fpCodes.includes(newFpCode.trim()) && fpCodes.length < maxFranchises) {
      setFpCodes([...fpCodes, newFpCode.trim().toUpperCase()])
      setNewFpCode('')
    }
  }

  const handleRemoveFpCode = (codeToRemove: string) => {
    setFpCodes(fpCodes.filter(code => code !== codeToRemove))
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              ⚖️ Health Score Comparison
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Compare health scores across multiple franchise partners
            </Typography>
          </Box>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Metric</InputLabel>
            <Select
              value={metric}
              onChange={(e) => setMetric(e.target.value as typeof metric)}
              label="Metric"
            >
              {metricOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        {/* FP Code Input */}
        <Box mb={3}>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <TextField
              size="small"
              label="Add Franchise Partner"
              placeholder="Enter FP code (e.g., AUPSC)"
              value={newFpCode}
              onChange={(e) => setNewFpCode(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddFpCode()}
              disabled={fpCodes.length >= maxFranchises}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddFpCode}
              disabled={!newFpCode.trim() || fpCodes.includes(newFpCode.trim()) || fpCodes.length >= maxFranchises}
            >
              Add
            </Button>
          </Stack>
          
          {/* Selected FP Codes */}
          {fpCodes.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Selected Franchise Partners ({fpCodes.length}/{maxFranchises}):
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {fpCodes.map(code => (
                  <Chip
                    key={code}
                    label={code}
                    onDelete={() => handleRemoveFpCode(code)}
                    color="primary"
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
        
        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={height}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Comparing health scores...
              </Typography>
            </Stack>
          </Box>
        )}
        
        {/* Chart */}
        {!loading && comparisonData && chartData.length > 0 && (
          <>
            <Box sx={{ width: '100%', height, mb: 3 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="displayName"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis
                    domain={[0, 100]}
                    label={{
                      value: 'Health Score',
                      angle: -90,
                      position: 'insideLeft'
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  {/* Reference lines for score thresholds */}
                  <ReferenceLine y={25} stroke="#4caf50" strokeDasharray="5 5" label="Good" />
                  <ReferenceLine y={50} stroke="#ff9800" strokeDasharray="5 5" label="Watch" />
                  <ReferenceLine y={75} stroke="#f44336" strokeDasharray="5 5" label="Poor" />
                  
                  <Bar
                    dataKey="score"
                    fill={(entry: any) => getBandColor(entry.band)}
                    name={`${metricOptions.find(m => m.value === metric)?.label} Score`}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            
            {/* Statistics Summary */}
            {comparisonData.statistics && Object.keys(comparisonData.statistics).length > 0 && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    📊 Comparison Statistics
                  </Typography>
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="body2" color="text.secondary">Count</Typography>
                      <Typography variant="h6">{comparisonData.statistics.count}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Average</Typography>
                      <Typography variant="h6">{comparisonData.statistics.average.toFixed(1)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Best</Typography>
                      <Typography variant="h6" color="#4caf50">{comparisonData.statistics.min.toFixed(1)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Worst</Typography>
                      <Typography variant="h6" color="#f44336">{comparisonData.statistics.max.toFixed(1)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Range</Typography>
                      <Typography variant="h6">{comparisonData.statistics.range.toFixed(1)}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}
            
            {/* Detailed Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Franchise Partner</strong></TableCell>
                    <TableCell><strong>FP Code</strong></TableCell>
                    <TableCell><strong>Market</strong></TableCell>
                    <TableCell><strong>Region</strong></TableCell>
                    <TableCell align="center"><strong>Score</strong></TableCell>
                    <TableCell align="center"><strong>Band</strong></TableCell>
                    <TableCell align="center"><strong>Exceptions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.map((entry) => (
                    <TableRow key={entry.fp_code}>
                      <TableCell>{entry.franchise_name}</TableCell>
                      <TableCell>{entry.fp_code}</TableCell>
                      <TableCell>{entry.market}</TableCell>
                      <TableCell>{entry.region_code}</TableCell>
                      <TableCell align="center">
                        {entry.score !== null ? entry.score.toFixed(1) : 'N/A'}
                      </TableCell>
                      <TableCell align="center">
                        <HealthScoreBadge
                          band={entry.band as any}
                          color={entry.color as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">{entry.exception_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Missing FPs Warning */}
            {comparisonData.missing_fp_codes.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Not found:</strong> {comparisonData.missing_fp_codes.join(', ')}
                </Typography>
              </Alert>
            )}
          </>
        )}
        
        {/* Empty State */}
        {!loading && fpCodes.length < 2 && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={height}>
            <Stack alignItems="center" spacing={2}>
              <CompareIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography variant="body1" color="text.secondary" textAlign="center">
                Add at least 2 franchise partners to compare their health scores
              </Typography>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default HealthScoreComparison